import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';
import { useInView } from 'react-intersection-observer';
import { Virtuoso } from 'react-virtuoso';
import { gql } from '@soundxyz/gql-string';
import { enumChecker } from '@soundxyz/utils/constants/enums';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { styled } from '@components-shared/src/stitches';
import { BondingCurveSaleList } from '@components/Feed/ListViews/BondingCurveSale';
import { PlaylistCreatedList } from '@components/Feed/ListViews/Playlist/PlaylistCreated';
import { PlaylistLikedList } from '@components/Feed/ListViews/Playlist/PlaylistLiked';
import { ReleasesAddedRemovedList } from '@components/Feed/ListViews/Playlist/ReleasesAddedRemoved';
import { ProfileCreatedPostActivity } from '@components/Feed/ListViews/Posts/CreatedPost';
import { PostReposted } from '@components/Feed/ListViews/Posts/CreatedPost/Repost';
import { PrimarySaleList } from '@components/Feed/ListViews/PrimarySale';
import { ReleaseLikedList } from '@components/Feed/ListViews/ReleaseLiked';
import { ReleasesAddedList } from '@components/Feed/ListViews/ReleasesAdded';
import { SecondarySaleList } from '@components/Feed/ListViews/SecondarySale';
import { BondingCurveSoldList } from '@components/Feed/ListViews/SoldBondingCurveNft';
import { EmptyState } from '@components/Feed/ListViews/components/EmptyState';
import { Refresh } from '@components/Feed/ListViews/components/Refresh';
import { FeedListSkeleton } from '@components/Feed/ListViews/components/Skeleton';
import { FeedError } from '@components/Feed/Placeholders';
import type { CollectorActivityFeedFilterArgs, FragmentType } from '@graphql/reactQuery';
import {
  CollectorActivityFeedDocument,
  CollectorActivityFeedTypeFilterOption,
  CollectorActivityUserInfoFragmentDoc,
  getFragment,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { usePopState } from '@hooks/usePopState';
import { TEMPORARY_SUCCESS_STATE_MS } from '@utils/constants';
import { SectionHeader } from '../SectionHeader';
import { ActivityFeed } from '../shared';
import { FeedHeader } from './FeedHeader';
import type { Action } from './store';
import { CollectorActivityState, useCollectorActivityState } from './store';

/*#__PURE__*/ gql(/* GraphQL */ `
  query CollectorActivityFeed(
    $userId: UUID!
    $pagination: CursorConnectionArgs!
    $filter: CollectorActivityFeedFilterArgs
  ) {
    collectorActivityFeed(pagination: $pagination, userId: $userId, filter: $filter) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          __typename
          id
          date
          user {
            id
          }
          ... on PrimarySale {
            id
            release {
              id
            }

            ...PrimarySaleList
          }
          ... on BondingCurveSale {
            id
            release {
              id
            }
            ...BondingCurveSaleList
          }
          ... on BondingCurveSold {
            id
            release {
              id
            }
            ...BondingCurveSoldList
          }
          ... on SecondarySale {
            id
            purchasedFromUser {
              id
            }
            release {
              id
            }
            ...SecondarySaleList
          }
          ... on ReleasesAddedToPlaylist {
            id
            playlist {
              id
            }
            ...ReleasesAddedList
          }
          ... on ReleasesAddedRemovedFromPlaylist {
            id
            date
            numAddedReleases
            playlist {
              id
            }
            ...ReleasesAddedRemovedList
          }
          ... on PlaylistCreated {
            id
            playlist {
              id
            }
            ...PlaylistCreatedList
          }
          ... on PlaylistLiked {
            id
            playlist {
              id
            }
            ...PlaylistLikedList
          }
          ... on ReleaseLiked {
            id
            release {
              id
            }
            ...ReleaseLikedList
          }

          ... on CreatedPost {
            id
            ...ProfileCreatedPost
          }

          ... on PostReposted {
            id
            originalPost {
              id
            }
            repost {
              id
            }
            ...PostRepostedInfo
          }
        }
      }
    }
  }

  fragment CollectorActivityUserInfo on User {
    id
    webappUri
  }
`);

export const ActionComponent = ({ item }: { item: Action }) => {
  switch (item.__typename) {
    case 'PrimarySale':
      return <PrimarySaleList key={item.id} activityFrag={item} />;

    case 'SecondarySale':
      return <SecondarySaleList key={item.id} activityFrag={item} />;

    case 'BondingCurveSale':
      return <BondingCurveSaleList key={item.id} activityFrag={item} />;

    case 'BondingCurveSold':
      return <BondingCurveSoldList key={item.id} activityFrag={item} />;

    case 'ReleasesAddedToPlaylist':
      // TODO: remove this check once we have a way to fix this on the api side
      if (!item.user) {
        // eslint-disable-next-line no-console
        console.error(`${item.id} does not include a playlist`);
        return <Placeholder />;
      }

      return item.playlist ? (
        <ReleasesAddedList key={item.id} activityFrag={item} playlistFeed={false} />
      ) : null;

    case 'ReleasesAddedRemovedFromPlaylist':
      if (!item.playlist) return null;
      return item.numAddedReleases > 0 ? (
        <ReleasesAddedRemovedList key={item.id} activityFrag={item} />
      ) : null;

    case 'PlaylistCreated':
      return item.playlist ? (
        <PlaylistCreatedList key={item.id} activityFrag={item} view="collector" />
      ) : null;

    case 'ReleaseLiked':
      return item.release ? <ReleaseLikedList key={item.id} activityFrag={item} /> : null;

    case 'PlaylistLiked':
      return item.playlist ? <PlaylistLikedList key={item.id} activityFrag={item} /> : null;

    case 'CreatedPost':
      return (
        <ProfileCreatedPostActivity
          postLinkSource="Collector Activity Feed"
          hideArtistOnPill={false}
          activityFrag={item}
        />
      );

    case 'PostReposted':
      return <PostReposted key={item.id} repostFrag={item} />;

    default:
      return null;
  }
};

const ItemContent = ({ index, item, userId }: { index: number; item: Action; userId: string }) => {
  const { inView, ref } = useInView({ rootMargin: '-45% -5px' });

  useEffect(() => {
    if (inView) {
      CollectorActivityState.scrollIndices[userId] = index;
    }
  }, [inView, index, userId]);

  return (
    <div ref={ref}>
      <ActionComponent item={item} />
    </div>
  );
};

const renderItemContent = (index: number, item: Action, userId: string) => (
  <ItemContent index={index} item={item} userId={userId} />
);

export const isCollectorActivityFeedTypeFilterOption = enumChecker(
  CollectorActivityFeedTypeFilterOption,
);

export const CollectorActivitySection = ({
  user,
}: {
  user: FragmentType<CollectorActivityUserInfoFragmentDoc>;
}) => {
  const [isFetchingNewActivity, setIsFetchingNewActivity] = useState(false);
  const [showRefreshSuccess, setShowRerefreshSuccess] = useState(false);

  const snapshot = useCollectorActivityState();

  const { isPopStateEvent } = usePopState();

  const { id: userId, webappUri } = getFragment(CollectorActivityUserInfoFragmentDoc, user);

  const setFilter = useCallback(
    (selection: CollectorActivityFeedTypeFilterOption[]) => {
      if (isEqual(snapshot.filter.types, selection)) return;
      const newActivityFeedGroupFilter: CollectorActivityFeedFilterArgs['types'] =
        selection?.filter(option => option !== CollectorActivityFeedTypeFilterOption.All);
      CollectorActivityState.filter[userId] = { types: newActivityFeedGroupFilter };
    },
    [userId, snapshot.filter.types],
  );

  const filter: CollectorActivityFeedFilterArgs = {
    types: snapshot.filter[userId]?.types?.length
      ? snapshot.filter[userId]?.types
      : [CollectorActivityFeedTypeFilterOption.All], // Fall back to All if no types are selected
  };

  const {
    orderedList: queryData,
    isLoadingNewPage: loading,
    refetch,
    error,
    hasNextPage = false,
    loadMoreNextPage: loadMore,
  } = useInfiniteQuery(CollectorActivityFeedDocument, {
    getNextPageParam({ data: { collectorActivityFeed } }) {
      return (
        collectorActivityFeed.pageInfo.hasNextPage && {
          after: collectorActivityFeed.pageInfo.endCursor,
        }
      );
    },
    filterQueryKey: {
      filter,
      userId,
    },
    variables({ pageParam }) {
      const after = pageParam?.after || null;
      return {
        userId,
        filter,
        pagination: {
          first: after == null ? 20 : 10,
          after,
        },
      };
    },
    list({ collectorActivityFeed }) {
      return collectorActivityFeed.edges
        .map(edge => edge.node)
        .filter(data => {
          switch (data.__typename) {
            case 'PostReposted': {
              // Filter out these cases
              return data.originalPost != null && data.repost != null;
            }
            default: {
              return true;
            }
          }
        });
    },
    uniq({ id }) {
      return id;
    },
    order: [
      [v => v.date, v => v.id],
      ['desc', 'desc'],
    ],
    staleTime: 0,
  });

  const refetchAndReset = useStableCallback(async () => {
    setIsFetchingNewActivity(true);

    // etch the initial page of data from the server.
    await refetch();

    // 5. provide visual feedback for refetch
    await new Promise(resolve => setTimeout(resolve, 200));

    setShowRerefreshSuccess(true);
    setIsFetchingNewActivity(false);
  });

  const memoizedFooter = useCallback(() => {
    if (!loading && queryData.length === 0) return <EmptyState />;
    if (!loading && !hasNextPage) return null;
    return <FeedListSkeleton rows={5} />;
  }, [loading, hasNextPage, queryData]);

  // reset the refresh success state
  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowRerefreshSuccess(false);
    }, TEMPORARY_SUCCESS_STATE_MS);
    return () => clearTimeout(timeout);
  }, [showRefreshSuccess]);

  const showError = !!error && queryData.length === 0 && !loading;

  const snapshotFilterType = snapshot.filter[userId]?.types;
  const memoizedCollectorActivityFeedType = useMemo(() => {
    return snapshotFilterType?.filter(isCollectorActivityFeedTypeFilterOption) || [];
  }, [snapshotFilterType]);

  return (
    <>
      <SectionHeader header="Activity" userWebappUri={webappUri}>
        <Refresh
          showRefreshSuccess={showRefreshSuccess}
          isFetchingNewActivity={isFetchingNewActivity}
          refetch={refetchAndReset}
        />
      </SectionHeader>

      <FeedHeader filter={memoizedCollectorActivityFeedType} setFilter={setFilter} />

      {showError && <FeedError divider />}
      <ActivityFeed>
        <Virtuoso
          useWindowScroll
          endReached={loadMore}
          // Scroll to global state scroll index if popstate event (user used back or forward button in browser)
          // Used to avoid scroll jumping when user switches between tabs
          // Virtuoso breaks if we pass undefined, so we pass an empty object instead

          data={queryData}
          itemContent={(index, item) => renderItemContent(index, item, userId)}
          components={{
            Footer: memoizedFooter,
          }}
          increaseViewportBy={700}
          {...(isPopStateEvent && snapshot.scrollIndices[userId]
            ? {
                initialTopMostItemIndex: {
                  index: snapshot.scrollIndices[userId] || 0,
                  align: 'center',
                },
              }
            : {})}
        />
        {/* Render server side feed without virtualized list */}
        {typeof window === 'undefined' &&
          queryData.map(item => <ActionComponent key={item.id} item={item} />)}
      </ActivityFeed>
    </>
  );
};

const Placeholder = styled('div', {
  height: 1,
  backgroundColor: '$white',
});
